import React, { useState, useEffect, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logout } from '../../ducks/auth.duck';

import logoImg from '../../assets/dhobara-logo.png';
import embroideryImg from '../../assets/dhobara-sellers-embroidery.jpg';
import formalImg from '../../assets/dhobara-formal-attire.jpg';

// Collection card images
import collBridalImg from '../../assets/dhobara-hero-bridal.jpg';
import collFormalImg from '../../assets/dhobara-formal-attire.jpg';
import collCasualImg from '../../assets/dhobara-sage-pink-dupatta.jpg';
import collMensImg from '../../assets/dhobara-mens-sherwani-ivory.jpg';
import collKidsImg from '../../assets/dhobara-cream-kurta.jpg';

import { getCartCount } from '../../util/cart';
import { Page } from '../../components';
import DhobaraFooter from '../../components/DhobaraFooter/DhobaraFooter';

// Designer logos
import logoSabyasachi from '../../assets/designers/sabyasachi-logo-freelogovectors.net.png';
import logoHSY from '../../assets/designers/HSY-logo.webp';
import logoAnitaDongre from '../../assets/designers/Anita_Dongre_logo.jpg';
import logoElan from '../../assets/designers/Elan-Logo.svg';
import logoSaniaMaskatiya from '../../assets/designers/sania_maskatiya.webp';
import logoNomiAnsari from '../../assets/designers/0008457_nomi-ansari.png';
import logoMariaB from '../../assets/designers/mariab_logo.jpg';
import logoRastah from '../../assets/designers/Rastah.png';
import logoMishaLakhani from '../../assets/designers/Misha_Lakhani_logo.avif';
import logoAsimJofa from '../../assets/designers/Asim_Jofa_logo.webp';

import VerifyEmailBanner from '../../components/VerifyEmailBanner/VerifyEmailBanner';
import css from './DhobaraHomePage.module.css';

// ── Icons (inline SVG, no external deps) ──────────────────────────────────────

const IconCamera = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="15" rx="2" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="8.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const IconSparkles = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5z" />
    <path d="M18.5 15l.75 2.25L21.5 18l-2.25.75L18.5 21l-.75-2.25L15.5 18l2.25-.75z" />
  </svg>
);

const IconBag = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2l1.5 4H19l-2 7H7L5 5H3" />
    <circle cx="9" cy="20" r="1.5" />
    <circle cx="17" cy="20" r="1.5" />
  </svg>
);

const IconPackage = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 7l-9-4-9 4v10l9 4 9-4V7z" />
    <path d="M3 7l9 4 9-4M12 11v10" />
  </svg>
);

// Mobile nav menu toggle icons (Task #148 — homepage had no mobile menu at all)
const IconChevron = () => (
  <svg viewBox="0 0 10 7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="10" height="7">
    <path d="M1 1l4 4 4-4" />
  </svg>
);

const IconMenu = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
    <path d="M3 6h18M3 12h18M3 18h18" />
  </svg>
);

const IconClose = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

// ── Data ──────────────────────────────────────────────────────────────────────

const SELLER_STEPS = [
  { icon: IconCamera, label: 'STEP 1', title: 'Snap & Submit', body: 'Take photos of your designer piece and submit the listing form. It takes under 2 minutes.' },
  { icon: IconSparkles, label: 'STEP 2', title: 'We Perfect It', body: 'Our AI cleans up your photos — background removal, color correction, studio-quality finish.' },
  { icon: IconBag, label: 'STEP 3', title: 'It Sells', body: 'Your piece goes live on Dhobara. We handle the buyer experience and secure payment.' },
  { icon: IconPackage, label: 'STEP 4', title: 'Ship & Done', body: 'Once sold, we send you a prepaid shipping label. Pack it, ship it, get paid.' },
];

const BUYER_STEPS = [
  { icon: IconBag, label: 'STEP 1', title: 'Browse & Discover', body: 'Explore authenticated South Asian designer pieces — bridal, formal, casual, menswear, and kids.' },
  { icon: IconSparkles, label: 'STEP 2', title: 'Buy with Confidence', body: 'Every item is AI-verified for authenticity. Secure checkout with card, Apple Pay, or Google Pay.' },
  { icon: IconPackage, label: 'STEP 3', title: 'We Handle Shipping', body: "The seller ships directly to you via UPS. You get a tracking number the moment it's sent." },
  { icon: IconCamera, label: 'STEP 4', title: 'Enjoy or Return', body: 'Confirm receipt to release payment. Not happy? Open a return request and we\'ll take care of it.' },
];

const DESIGNERS = [
  { name: 'Sabyasachi',      slug: 'sabyasachi',       logo: logoSabyasachi },
  { name: 'HSY',             slug: 'hassan-sheheryar-yasin-hsy', logo: logoHSY },
  { name: 'Anita Dongre',   slug: 'anita-dongre',      logo: logoAnitaDongre, xlarge: true },
  { name: 'Élan',            slug: 'elan',              logo: logoElan },
  { name: 'Sania Maskatiya', slug: 'sania-maskatiya',   logo: logoSaniaMaskatiya, large: true },
  { name: 'Nomi Ansari',    slug: 'nomi-ansari',        logo: logoNomiAnsari },
  { name: 'Maria B.',        slug: 'maria-b',           logo: logoMariaB, large: true },
  { name: 'Asim Jofa',      slug: 'asim-jofa',          logo: logoAsimJofa },
  { name: 'Rastah',          slug: 'rastah',            logo: logoRastah },
  { name: 'Misha Lakhani',  slug: 'misha-lakhani',      logo: logoMishaLakhani },
];

const COLLECTIONS = [
  { label: 'Bridal', to: '/collection/bridal', image: collBridalImg, position: 'center center' },
  { label: 'Formal', to: '/collection/formal', image: collFormalImg, position: 'center 30%' },
  { label: 'Casual', to: '/collection/casual', image: collCasualImg, position: '58% center',    tint: true },
  { label: 'Mens',   to: '/collection/mens',   image: collMensImg,   position: 'center center', tint: true },
  { label: 'Kids',   to: '/collection/kids',   image: collKidsImg,   position: 'center center', tint: true },
];

const NAV_COLLECTIONS = [
  { label: 'Bridal', to: '/collection/bridal' },
  { label: 'Formal', to: '/collection/formal' },
  { label: 'Casual', to: '/collection/casual' },
  { label: 'Mens', to: '/collection/mens' },
  { label: 'Kids', to: '/collection/kids' },
];

// ── Components ────────────────────────────────────────────────────────────────

const SellBuyBlock = () => {
  const [mode, setMode] = useState('sellers');

  return (
    <section id="sell" className={css.dualSection}>
      <div className={css.dualGrid}>
        <div className={css.dualText}>

          {/* Toggle */}
          <div className={css.toggleBar}>
            <div className={css.toggleWrap}>
              <button
                className={`${css.toggleBtn} ${mode === 'sellers' ? css.toggleBtnActive : ''}`}
                onClick={() => setMode('sellers')}
              >
                For Sellers
              </button>
              <button
                className={`${css.toggleBtn} ${mode === 'buyers' ? css.toggleBtnActive : ''}`}
                onClick={() => setMode('buyers')}
              >
                For Buyers
              </button>
            </div>
          </div>

          {/* Sellers panel */}
          <div className={mode === 'sellers' ? css.panelVisible : css.panelHidden}>
            <h2 className={css.dualTitle}>
              Your Wardrobe Deserves<br />
              a <em>Second Life</em>
            </h2>
            <p className={css.dualBody}>
              That Sabyasachi lehenga you wore once? Someone is looking for it right now.
              List it in minutes, we handle everything else — from photo editing to shipping labels.
            </p>
            <div className={css.btnRow}>
              <Link to="/dashboard" className={css.btnLight}>Start Selling</Link>
              <Link to="/how-it-works" className={css.btnOutlineLight}>Learn More</Link>
            </div>
          </div>

          {/* Buyers panel */}
          <div className={mode === 'buyers' ? css.panelVisible : css.panelHidden}>
            <h2 className={css.dualTitle}>
              Designer at a<br />
              <em>Fraction of the Price</em>
            </h2>
            <p className={css.dualBody}>
              Authentic South Asian luxury, pre-loved and ready for its next chapter.
              Browse curated collections from the designers you already love.
            </p>
            <div className={css.buyerCollectionTags}>
              {COLLECTIONS.map(c => (
                <Link key={c.to} to={c.to} className={css.buyerCollectionTag}>{c.label}</Link>
              ))}
            </div>
          </div>
        </div>

        {/* Image */}
        <div className={css.dualImage}>
          <img
            src={mode === 'sellers' ? embroideryImg : formalImg}
            alt={mode === 'sellers' ? 'Gold embroidery on silk' : 'Formal attire collection'}
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

// Hero background video. Phones and portrait tablets get the full portrait
// clip; landscape screens get a 16:9 version (sharp clip right of centre,
// blurred fill behind). Files live in public/static/media/hero.
//
// The poster <picture> is server-rendered so the hero is never empty. The
// video only gets a src on the client, and only fades in once it is actually
// playing - so Low Power Mode, data saver and reduced-motion visitors keep
// the still image instead of a blank box.
const HERO_MEDIA = '/static/media/hero';
const HERO_LANDSCAPE_QUERY = '(orientation: landscape)';

// Keep in step with .nav's height in DhobaraHomePage.module.css.
const NAV_HEIGHT = 72;

const HeroVideo = () => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || typeof window === 'undefined' || !window.matchMedia) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = navigator.connection && navigator.connection.saveData;
    if (reduceMotion || saveData) return;

    let cancelled = false;
    const onPlaying = () => setPlaying(true);

    const start = () => {
      if (cancelled) return;
      const landscape = window.matchMedia(HERO_LANDSCAPE_QUERY).matches;
      const base = `${HERO_MEDIA}/${landscape ? 'hero-desktop-full' : 'hero-mobile'}`;
      // React doesn't reliably emit the muted attribute, and iOS only
      // autoplays muted video, so set it on the element directly.
      video.muted = true;
      const canWebm = video.canPlayType('video/webm; codecs="vp9"');
      video.src = `${base}${canWebm ? '.webm' : '.mp4'}`;

      video.addEventListener('playing', onPlaying);
      const attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(() => {});
    };

    // Hold the clip back until the rest of the page has finished loading.
    // The copy and the poster are server-rendered, so they paint first and
    // decide LCP; the video must never compete with them for bandwidth.
    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener('load', start);
      video.removeEventListener('playing', onPlaying);
    };
  }, []);

  return (
    <>
      <picture>
        <source media={HERO_LANDSCAPE_QUERY} srcSet={`${HERO_MEDIA}/hero-desktop-full-poster.jpg`} />
        <img
          className={css.heroMedia}
          fetchpriority="high"
          src={`${HERO_MEDIA}/hero-mobile-poster.jpg`}
          alt="Model carrying a Dhobara shopping bag"
        />
      </picture>
      <video
        ref={videoRef}
        className={`${css.heroMedia} ${css.heroVideo}${playing ? ` ${css.heroVideoPlaying}` : ''}`}
        muted
        loop
        playsInline
        autoPlay
        preload="none"
        aria-hidden="true"
      />
    </>
  );
};

const DhobaraHomePage = ({ isAuthenticated, currentUser }) => {
  const displayName = currentUser?.attributes?.profile?.firstName || null;
  const dispatch = useDispatch();
  const history = useHistory();
  const handleLogout = () => { dispatch(logout()).then(() => history.push('/')); };
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [collectionsOpen, setCollectionsOpen] = useState(false);
  const [howTab, setHowTab] = useState('sellers');
  const closeMenu = () => { setMobileMenuOpen(false); setCollectionsOpen(false); };

  const [cartCount, setCartCount] = useState(0);
  useEffect(() => {
    const update = () => setCartCount(getCartCount());
    update();
    window.addEventListener('storage', update);
    return () => window.removeEventListener('storage', update);
  }, []);

  // Task #165: on mobile the header is absolutely positioned so it can sit
  // transparently over the hero video - white type, knocked-out wordmark -
  // and picks up the cream background and dark type only once the visitor has
  // scrolled past the hero. The threshold is the hero's own height, not a
  // fixed 40px: the hero is 100vh, so 40px turned the bar cream while a full
  // screen of dark video was still behind it.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      const heroHeight = window.innerHeight - NAV_HEIGHT;
      setScrolled(window.scrollY > heroHeight);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  // Pre-launch $5-per-listing promo card: pops in from the right as the
  // visitor scrolls, auto-hides after a few seconds, then pops back in again
  // every time they cross the next scroll threshold - a periodic nudge
  // rather than a one-time popup. PROMO_SCROLL_STEP controls how far apart
  // each re-appearance is; PROMO_VISIBLE_MS controls how long it stays up.
  //
  // Capped at PROMO_MAX_SHOWS appearances so it doesn't feel relentless on a
  // long scroll session. The count lives in localStorage (not just in-memory
  // state) so it survives page refreshes and closing/reopening the tab, and
  // rolls over automatically PROMO_RESET_MS after the count started - so a
  // visitor who comes back several hours later sees the full 3
  // appearances again rather than being permanently capped.
  const [promoVisible, setPromoVisible] = useState(false);
  useEffect(() => {
    const PROMO_SCROLL_STEP = 700;
    const PROMO_VISIBLE_MS = 6000;
    const PROMO_MAX_SHOWS = 3;
    const PROMO_RESET_MS = 12 * 60 * 60 * 1000; // 12 hours
    const PROMO_STORAGE_KEY = 'dhobaraPromoShowState';

    const readPromoState = () => {
      try {
        const raw = window.localStorage.getItem(PROMO_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        const windowStart = parsed && parsed.windowStart ? parsed.windowStart : 0;
        const expired = Date.now() - windowStart > PROMO_RESET_MS;
        return !parsed || expired ? { count: 0, windowStart: Date.now() } : parsed;
      } catch (e) {
        // Storage unavailable (e.g. private browsing) - fall back to
        // showing the full count for just this pageview rather than
        // breaking the promo entirely.
        return { count: 0, windowStart: Date.now() };
      }
    };

    const writePromoState = state => {
      try {
        window.localStorage.setItem(PROMO_STORAGE_KEY, JSON.stringify(state));
      } catch (e) {
        // Ignore - worst case the count just doesn't persist this session.
      }
    };

    let promoState = readPromoState();
    let lastStep = 0;
    let hideTimer = null;
    const showPromo = () => {
      if (promoState.count >= PROMO_MAX_SHOWS) return;
      promoState = { count: promoState.count + 1, windowStart: promoState.windowStart };
      writePromoState(promoState);

      setPromoVisible(true);
      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = setTimeout(() => setPromoVisible(false), PROMO_VISIBLE_MS);
    };
    const handlePromoScroll = () => {
      if (promoState.count >= PROMO_MAX_SHOWS) return;
      const step = Math.floor(window.scrollY / PROMO_SCROLL_STEP);
      if (step > lastStep && window.scrollY > 200) {
        lastStep = step;
        showPromo();
      }
    };
    window.addEventListener('scroll', handlePromoScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handlePromoScroll);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, []);

  return (
    <Page
      title="Dhobara | Pre-Owned South Asian Designer Wear, Authenticated"
      description="Shop authenticated, gently worn bridal lehengas, sarees, sherwanis & occasion wear from top Pakistani & Indian designers at 40–70% off retail. List your designer pieces and sell with Dhobara."
      scrollingDisabled={false}
    >
    <div className={css.page}>

      {/* Renders nothing unless the signed-in user's email is unverified. When
          it does render, the CSS below takes .nav out of its absolute overlay
          so the banner isn't hidden behind it - the hero simply starts lower
          for that user. */}
      <VerifyEmailBanner />

      {/* NAV */}
      <header className={scrolled ? `${css.nav} ${css.navScrolled}` : css.nav}>
        <Link to="/" className={css.navLogo}>
          <img src={logoImg} alt="Dhobara" />
        </Link>
        <nav>
          <ul className={css.navLinks}>
            <li><Link to="/how-it-works">How It Works</Link></li>
            <li className={css.navDropdownWrapper}>
              <button type="button" className={css.navDropdownTrigger}>
                Collections <IconChevron />
              </button>
              <div className={css.navDropdownMenu}>
                {NAV_COLLECTIONS.map(c => (
                  <Link key={c.to} to={c.to} className={css.navDropdownItem}>{c.label}</Link>
                ))}
                <div className={css.navDropdownDivider} />
                <Link to="/s" className={css.navDropdownItem}>Browse All</Link>
              </div>
            </li>
            <li><Link to="/designers">Designers</Link></li>
            <li><Link to="/why-dhobara">Why Dhobara?</Link></li>
          </ul>
        </nav>
        <div className={css.navRight}>
          <Link
            to={isAuthenticated ? '/cart' : '/login'}
            className={css.savedItemsLink}
            aria-label="Saved items"
          >
            <span className={css.savedItemsIconWrap}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              {cartCount > 0 && <span className={css.savedItemsBadge}>{cartCount}</span>}
            </span>
            <span className={css.savedItemsLabel}>Saved Items</span>
          </Link>
          <Link to="/dashboard" className={css.navCta}>Sell With Us</Link>
          {isAuthenticated ? (
            <div className={css.userMenuWrapper}>
              <button type="button" className={css.userMenuTrigger}>
                {displayName ? `Hi, ${displayName}` : 'Account'} <IconChevron />
              </button>
              <div className={css.userMenuDropdown}>
                <Link to="/dashboard" className={css.userMenuDropdownItem}>Dashboard</Link>
                <Link to="/account" className={css.userMenuDropdownItem}>Account Settings</Link>
                <div className={css.userMenuDropdownDivider} />
                <button type="button" className={css.userMenuDropdownBtn} onClick={handleLogout}>Log Out</button>
              </div>
            </div>
          ) : (
            <>
              <Link to="/signup" className={css.navAuthLink}>Sign up</Link>
              <Link to="/login" className={css.navAuthLink}>Log in</Link>
            </>
          )}
          <button
            type="button"
            className={css.navMenuBtn}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(open => !open)}
          >
            {mobileMenuOpen ? <IconClose /> : <IconMenu />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className={css.mobileMenu}>
            <Link to="/how-it-works" onClick={closeMenu}>How It Works</Link>
            <div className={css.mobileCollectionsGroup}>
              <button
                type="button"
                className={css.mobileCollectionsTrigger}
                onClick={() => setCollectionsOpen(o => !o)}
              >
                <span>Collections</span>
                <span className={collectionsOpen ? css.mobileChevronOpen : css.mobileChevron}>
                  <IconChevron />
                </span>
              </button>
              {collectionsOpen && (
                <div className={css.mobileCollectionsDrawer}>
                  {NAV_COLLECTIONS.map(c => (
                    <Link key={c.to} to={c.to} onClick={closeMenu} className={css.mobileCollectionItem}>{c.label}</Link>
                  ))}
                  <Link to="/s" onClick={closeMenu} className={css.mobileCollectionItem}>Browse All</Link>
                </div>
              )}
            </div>
            <Link to="/designers" onClick={closeMenu}>Designers</Link>
            <Link to="/why-dhobara" onClick={closeMenu}>Why Dhobara?</Link>
            <Link to="/cart" onClick={closeMenu}>Saved Items</Link>
            <div className={css.mobileMenuDivider} />
            <Link to="/dashboard" onClick={closeMenu}>Sell With Us</Link>
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" onClick={closeMenu}>Dashboard</Link>
                <Link to="/account" onClick={closeMenu}>Account Settings</Link>
                <button type="button" className={css.mobileMenuBtn} onClick={() => { closeMenu(); handleLogout(); }}>Log Out</button>
              </>
            ) : (
              <>
                <Link to="/signup" onClick={closeMenu}>Sign up</Link>
                <Link to="/login" onClick={closeMenu}>Log in</Link>
              </>
            )}
          </div>
        )}
      </header>

      {/* HERO */}
      <section className={css.hero}>
        <HeroVideo />
        <div className={css.heroOverlay} />
        <div className={css.heroContent}>
          <span className={css.heroEyebrow}>Pre-Owned South Asian Luxury</span>
          <h1 className={css.heroHeadline}>Luxury deserves a second life.</h1>
          <p className={css.heroSub}>South Asian designer fashion, rediscovered.</p>
          <div className={css.heroButtons}>
            <Link to="/dashboard" className={css.btnLight}>Start Selling</Link>
            <Link to="/how-it-works" className={css.btnOutlineLight}>How It Works</Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      {/* SHOP BY COLLECTION */}
      <section className={css.collectionsSection}>
        <div className={css.collectionsSectionInner}>
          <div className={css.collectionsGrid}>
            {COLLECTIONS.map(c => (
              <Link key={c.to} to={c.to} className={`${css.collectionCard}${c.tint ? ` ${css.collectionCardTinted}` : ''}`} style={c.zoom ? { '--img-zoom': c.zoom } : undefined}>
                <img
                  src={c.image}
                  alt={c.label}
                  className={css.collectionCardImg}
                  style={{ objectPosition: c.position }}
                />
                <div className={css.collectionCardOverlay} />
                <div className={css.collectionCardFade} />
                <span className={css.collectionCardLabel}>{c.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section id="how" className={css.howSection}>
        <div className={css.sectionInner}>
          <span className={css.eyebrow} style={{ textAlign: 'center', display: 'block' }}>Effortlessly Simple</span>
          <h2 className={css.sectionTitle}>How It Works</h2>
          <div className={css.howToggle}>
            <button
              className={howTab === 'sellers' ? `${css.howToggleBtn} ${css.howToggleBtnActive}` : css.howToggleBtn}
              onClick={() => setHowTab('sellers')}
            >
              For Sellers
            </button>
            <button
              className={howTab === 'buyers' ? `${css.howToggleBtn} ${css.howToggleBtnActive}` : css.howToggleBtn}
              onClick={() => setHowTab('buyers')}
            >
              For Buyers
            </button>
          </div>
          <div className={css.stepsGrid}>
            {(howTab === 'sellers' ? SELLER_STEPS : BUYER_STEPS).map(s => (
              <div key={s.title} className={css.step}>
                <div className={css.stepIcon}><s.icon /></div>
                <p className={css.stepLabel}>{s.label}</p>
                <h3 className={css.stepName}>{s.title}</h3>
                <p className={css.stepDesc}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DESIGNERS */}
      <section id="designers" className={css.designersSection}>
        <div className={css.sectionInner}>
          <span className={css.eyebrow} style={{ textAlign: 'center', display: 'block' }}>Curated Excellence</span>
          <h2 className={css.sectionTitle}>The Designers You Love</h2>
          <div className={css.designersGrid}>
            {DESIGNERS.map(d => (
              <Link key={d.name} to={`/designer/${d.slug}`} className={css.designerCell}>
                <img
                  src={d.logo}
                  alt={d.name}
                  loading="lazy"
                  style={
                    d.xlarge ? { maxHeight: '156px', maxWidth: '260px', width: 'auto', objectFit: 'contain' } :
                    d.large  ? { maxHeight: '72px',  maxWidth: '180px', width: 'auto', objectFit: 'contain' } :
                               { maxHeight: '52px',  maxWidth: '140px', width: 'auto', objectFit: 'contain' }
                  }
                />
              </Link>
            ))}
          </div>
          <p className={css.designerMore}>
            And many more — from Karachi to Delhi to New York.
          </p>
          <div style={{ textAlign: 'center', marginTop: '36px' }}>
            <Link to="/designers" className={css.btnOutline}>Designers We Accept</Link>
          </div>
        </div>
      </section>

      {/* SELL / BUY TOGGLE */}
      <SellBuyBlock />

      {/* FOOTER */}
      <DhobaraFooter />

      {/* PROMO CARD - scroll-triggered, repeats */}
      <aside
        className={promoVisible ? `${css.promoCard} ${css.promoCardVisible}` : css.promoCard}
        aria-hidden={!promoVisible}
      >
        <button
          type="button"
          className={css.promoClose}
          onClick={() => setPromoVisible(false)}
          aria-label="Dismiss"
        >
          &times;
        </button>
        <div className={css.promoDivider} />
        <span className={css.promoEyebrow}>We&rsquo;re Live &middot; Limited Offer</span>
        <span className={css.promoPill}>Earn $5 Per Listing</span>
        <h3 className={css.promoHeading}>
          Curate your closet,<br />earn up to $50.
        </h3>
        <p className={css.promoBody}>
          Be part of our founding community. List your pre-owned South Asian luxury pieces today.
        </p>
        <Link to="/dashboard" className={css.promoBtn}>Start Listing &rarr;</Link>
        <p className={css.promoFootnote}>Only for the first 1,000 listings</p>
      </aside>

    </div>
    </Page>
  );
};

export default DhobaraHomePage;
